import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Clapperboard, Loader2, Sparkles, Volume2, AlertTriangle, CheckCircle2 } from "lucide-react";

function PromoPreview({ promo }) {
  const audioRef = useRef(null);
  const clip0 = (promo.clips || [])[0];
  const playVoice = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.volume = 1;
    el.play().catch(() => toast.error("Tap again — the browser blocked autoplay"));
  };
  return (
    <div className="rounded-sm border border-white/10 bg-black/30 p-4" data-testid={`promo-preview-${promo.key}`}>
      <div className="flex items-center gap-2">
        <span className={`rounded-sm px-2 py-0.5 font-oswald text-[10px] font-bold uppercase tracking-widest ${promo.voice_kind === "urgent" ? "bg-[#d90429] text-white" : "bg-[#1f63c9] text-white"}`}>
          {promo.voice_kind === "urgent" ? "Severe" : "Brand"}
        </span>
        <div className="font-oswald text-sm font-bold uppercase tracking-widest text-white">{promo.title}</div>
      </div>
      <div className="mt-3 grid grid-cols-[180px_1fr] gap-4">
        <div className="overflow-hidden rounded-sm border border-white/10 bg-black">
          {clip0 ? (
            <video src={`${BACKEND_URL}${clip0.url}`} muted loop autoPlay playsInline className="h-[100px] w-full object-cover" data-testid={`promo-clip-${promo.key}`} />
          ) : (
            <div className="flex h-[100px] items-center justify-center text-xs text-gray-500">No clip</div>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-xs leading-relaxed text-gray-400 line-clamp-3">{promo.voice_text}</div>
          <div className="mt-2 flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{(promo.clips || []).length} clips · voice: {promo.voice}</span>
            <button
              type="button"
              onClick={playVoice}
              data-testid={`promo-play-voice-${promo.key}`}
              className="inline-flex items-center gap-1.5 rounded-sm border border-white/15 px-2.5 py-1 font-oswald text-[10px] font-bold uppercase tracking-widest text-white hover:border-[#ffd60a] hover:text-[#ffd60a]"
            >
              <Volume2 className="h-3 w-3" /> Play voiceover
            </button>
          </div>
        </div>
      </div>
      <audio ref={audioRef} src={`${BACKEND_URL}${promo.voice_url}`} preload="none" />
    </div>
  );
}

export function PromoCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/promos");
      setData(data);
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Poll while a generation is in flight.
  useEffect(() => {
    const generating = data?.status === "generating";
    if (generating && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && d.status !== "generating") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (d.status === "ready") toast.success("Promos are ready — now airing in the loop");
          else if (d.status === "error") toast.error(d.error || "Generation failed");
        }
      }, 5000);
    }
    if (!generating && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [data?.status]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/promos/generate");
      setData((prev) => ({ ...(prev || {}), ...data }));
      toast.message("Generating cinematic promos — this takes a few minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const status = data.status || "idle";
  const generating = status === "generating";
  const ready = status === "ready" && (data.promos || []).length > 0;

  return (
    <div className="glass rounded-sm border border-[#ffd60a]/40 p-6" data-testid="promo-card">
      <div className="flex items-center gap-2">
        <Clapperboard className="h-5 w-5 text-[#ffd60a]" />
        <div className="font-oswald text-lg font-bold uppercase tracking-widest text-white">AI Promo Spots</div>
      </div>
      <p className="mt-1 text-sm text-gray-300">
        Two cinematic 30-second promos — "Severe Weather? We've Got You Covered" and "Georgia's 24/7 Weather Source" —
        generated with AI video + studio voiceover and aired as normal slides in the loop. Generate once; they're stored
        permanently and replayed forever (no credits spent per airing).
      </p>

      {/* Status strip */}
      <div className="mt-4 flex items-center gap-3 rounded-sm border border-white/10 bg-black/30 p-3" data-testid="promo-status">
        {generating ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-[#4cc9f0]" />
            <span className="text-sm text-gray-200">{data.stage || "Generating…"}</span>
          </>
        ) : ready ? (
          <>
            <CheckCircle2 className="h-4 w-4 text-[#22c55e]" />
            <span className="text-sm text-gray-200">Ready — airing in the loop{data.generated_at ? ` · generated ${new Date(data.generated_at).toLocaleString()}` : ""}</span>
          </>
        ) : status === "error" ? (
          <>
            <AlertTriangle className="h-4 w-4 text-[#d90429]" />
            <span className="text-sm text-gray-200">{data.error || "Generation failed"}</span>
          </>
        ) : (
          <span className="text-sm text-gray-400">Not generated yet — the promo slides stay hidden until you generate them.</span>
        )}
      </div>

      {ready && (
        <div className="mt-4 grid gap-3" data-testid="promo-previews">
          {(data.promos || []).map((p) => <PromoPreview key={p.key} promo={p} />)}
        </div>
      )}

      <button
        type="button"
        onClick={generate}
        disabled={busy || generating}
        data-testid="promo-generate-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-sm bg-[#ffd60a] px-4 py-2 font-oswald text-sm font-bold uppercase tracking-widest text-[#0a0f1c] transition hover:bg-[#ffe34d] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {generating ? "Generating…" : ready ? "Regenerate Promos" : "Generate Promos"}
      </button>
      <p className="mt-2 text-xs text-gray-500">
        Generation uses your Universal Key credits (six AI video clips + two voiceovers). Regenerating replaces the current spots.
      </p>
    </div>
  );
}
